import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { marked } from "marked";
import "../styles/BlogDetail.css";
import { getBlogImage } from "../utils/blogImages";
import api, { API_ENDPOINTS } from "../config/api";
import SEO from "./SEO";
import BackToTop from "./BackToTop";

// Initialize marked with default options
marked.use({
  gfm: true, // GitHub Flavored Markdown
  breaks: true, // Convert \n to <br>
});

const BlogDetail = () => {
  const { slug } = useParams();
  const [blog, setBlog] = useState({
    title: "",
    content: "",
    date: "",
    author: "",
    category: "",
    image: "",
    tags: [],
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchBlogContent = async () => {
      try {
        const response = await api.get(API_ENDPOINTS.BLOGS.DETAIL(slug));

        if (response.data) {
          setBlog({
            title: response.data.title || slug,
            content: response.data.content,
            category: getCategoryForBlog(response.data.title || slug),
            tags: generateTagsFromTitle(response.data.title || ""),
            date: response.data.date || new Date().toISOString().split("T")[0],
          });
        }
      } catch (error) {
        console.error(
          "Error fetching blog content:",
          error.response ? error.response.data : error.message
        );
        setError("Failed to load blog content. Please try again later.");
      } finally {
        setLoading(false);
      }
    };

    if (slug) {
      fetchBlogContent();
    }
  }, [slug]);

  // Function to assign a category based on title
  const getCategoryForBlog = (title) => {
    const categories = [
      "Education",
      "Study Abroad",
      "University",
      "Career",
      "Student Life",
      "Admissions",
    ];
    const index = title.length % categories.length;
    return categories[index];
  };

  // Function to generate tags from title
  const generateTagsFromTitle = (title) => {
    const commonTags = [
      "University",
      "Education",
      "Study Abroad",
      "Admissions",
      "Career",
      "Student Tips",
    ];
    // Select a subset of tags based on the title length
    const numTags = Math.min(3, Math.floor(title.length / 5));
    const tags = [];

    for (let i = 0; i < numTags; i++) {
      const index = (title.length + i) % commonTags.length;
      tags.push(commonTags[index]);
    }

    return tags;
  };

  const getReadingTime = (content) => {
    // Average reading speed is ~200-250 words per minute
    const words = content.split(/\s+/).length;
    const minutes = Math.max(1, Math.ceil(words / 200));
    return `${minutes} min read`;
  };

  if (loading)
    return (
      <div className="loading-indicator">
        <p>Loading blog content...</p>
      </div>
    );

  if (error)
    return (
      <div className="error-message">
        <p>{error}</p>
      </div>
    );

  return (
    <div className="blog-detail-container">
      <SEO
        title={`${blog.title} - Grad4Grad Blog`}
        description={`${blog.title} - Expert insights and guidance for international students. Read our comprehensive guide on study abroad topics, university admissions, and student life.`}
        keywords={`${blog.tags.join(
          ", "
        )}, study abroad, international students, university admissions, education blog`}
        image={getBlogImage(slug)}
        url={`https://grad4grad.com/blogs/${slug}`}
        type="article"
        blog={{
          title: blog.title,
          description: blog.title,
          image: getBlogImage(slug),
          publishedDate: blog.date,
          modifiedDate: blog.date,
        }}
      />
      <Link to="/blogs" className="back-to-blogs">
        ← Back to blogs
      </Link>

      <div className="blog-detail-header">
        <span className="blog-detail-category">{blog.category}</span>
        <h1 className="blog-detail-title">{blog.title}</h1>
        <div className="blog-detail-meta">
          <div>By {blog.author}</div>
          <div>{blog.date}</div>
          <div>{getReadingTime(blog.content)}</div>
        </div>
      </div>

      <div className="blog-detail-featured-image">
        <img
          src={getBlogImage(slug)}
          alt={blog.title}
          onError={(e) => {
            e.target.src = getBlogImage("default"); // Fallback to default image if image fails to load
          }}
        />
      </div>

      <div
        className="blog-detail-content"
        dangerouslySetInnerHTML={{
          __html: blog.content
            ? marked.parse(blog.content.replace(/^#\s+.+$/m, ""))
            : "<p>No content available for this blog post.</p>",
        }}
      />

      <div className="blog-detail-footer">
        <div className="blog-detail-tags">
          {blog.tags.map((tag, index) => (
            <span key={index} className="blog-detail-tag">
              {tag}
            </span>
          ))}
        </div>

        <div className="blog-detail-navigation">
          <Link to="/blogs">← Back to all blogs</Link>
        </div>
      </div>
      <BackToTop />
    </div>
  );
};

export default BlogDetail;
